// 如何将一个区、多区转换成mapboxgl需要的顶点、顶点索引(参考FillBucket)
import classifyRings from "../util/classify_rings";
import { StructArrayLayout2f8, TriangleIndexArray } from "./array_types";
import SegmentVector from "./segment";
import assert from "assert";
import MercatorCoordinate from "../geo/mercator_coordinate";
import earcut from "earcut";
// 最大内圈数量
const EARCUT_MAX_RINGS = 500;

/**
 * @description: 裁剪几何信息。此类主要是用于构建裁剪区的buffer。
 */
class ClippingFeature {
    constructor(options) {
        const opts = options || {};
        this.uploaded = false;
        this.layoutVertexArray = new StructArrayLayout2f8();
        this.indexArray = new TriangleIndexArray();
        this.segments = new SegmentVector();
        this.crs = opts.crs;
        if (!this.crs) {
            throw new Error("缺少crs参数");
        }
    }

    /**
     * @description: 销毁裁剪区
     * @return {*}
     */
    destroy() {
        this.layoutVertexArray.resize(0);
        this.indexArray.resize(0);
        if(this.layoutVertexBuffer){
            this.layoutVertexBuffer.destroy();
        }
        if(this.indexBuffer){
            this.indexBuffer.destroy();
        }
        this.segments.destroy();
        this.uploaded = false;
    }

    /**
     * @description: 根据webgl上下文创建buffer
     * @param {*} context
     * @param {*} layoutAttributes
     * @return {*}
     */
    upload(context, layoutAttributes) {
        if (!this.uploaded) {
            this.layoutVertexBuffer = context.createVertexBuffer(
                this.layoutVertexArray,
                layoutAttributes
            );
            this.indexBuffer = context.createIndexBuffer(this.indexArray);
        }
        this.uploaded = true;
    }

    /**
     * @description: 添加区要素几何信息
     * @param {Array<Array<Array<Number>>>} coordinates
     * @return {*}
     */
    addPolygonCoordinates(coordinates) {
        for (const polygon of classifyRings(coordinates, EARCUT_MAX_RINGS)) {
            let numVertices = 0;
            for (const ring of polygon) {
                numVertices += ring.length;
            }

            const triangleSegment = this.segments.prepareSegment(
                numVertices,
                this.layoutVertexArray,
                this.indexArray
            );
            const triangleIndex = triangleSegment.vertexLength;

            const flattened = [];
            const holeIndices = [];

            for (const ring of polygon) {
                if (ring.length === 0) {
                    continue;
                }

                if (ring !== polygon[0]) {
                    holeIndices.push(flattened.length / 2);
                }

                const coords = MercatorCoordinate.fromLngLat(
                    {
                        lng: ring[0][0],
                        lat: ring[0][1],
                    },
                    0,
                    this.crs
                );

                this.layoutVertexArray.emplaceBack(coords.x, coords.y);
                flattened.push(coords.x);
                flattened.push(coords.y);

                for (let i = 1; i < ring.length; i++) {
                    const pointCoords = MercatorCoordinate.fromLngLat(
                        {
                            lng: ring[i][0],
                            lat: ring[i][1],
                        },
                        0,
                        this.crs
                    );
                    this.layoutVertexArray.emplaceBack(
                        pointCoords.x,
                        pointCoords.y
                    );
                    flattened.push(pointCoords.x);
                    flattened.push(pointCoords.y);
                }
            }

            const indices = earcut(flattened, holeIndices);
            assert(indices.length % 3 === 0);

            for (let i = 0; i < indices.length; i += 3) {
                this.indexArray.emplaceBack(
                    triangleIndex + indices[i],
                    triangleIndex + indices[i + 1],
                    triangleIndex + indices[i + 2]
                );
            }

            triangleSegment.vertexLength += numVertices;
            triangleSegment.primitiveLength += indices.length / 3;
        }
    }
}

export default ClippingFeature;
